import { FC, useState } from 'react';
import { Close, Save } from '@mui/icons-material';
import { Grid, Box, Button, Typography, ButtonGroup } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useParams, useHistory } from 'react-router-dom';
import { postSalesContractOffer } from '../../../../../fetch';
import { formatMoney } from '../../../../../helpers';
import { SalesContractImages } from './sales-contract-images';

interface IApprovedDetails {
  statusText?: string;
  approvedStatusDetails: any;
  refetchApprovedDetails: () => void;
  refetchContractDetails: () => void;
  isLoadingApprovedDetails: boolean;
  isSaving: boolean;
  setIsSaving: any;
}

export const ApprovedDetails: FC<IApprovedDetails> = ({
  statusText,
  approvedStatusDetails,
  refetchApprovedDetails,
  refetchContractDetails,
  isLoadingApprovedDetails,
  isSaving,
  setIsSaving
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { buildId }: { buildId: string } = useParams();
  const [imageFormData, setImageFormData] = useState<any>();
  const { enqueueSnackbar } = useSnackbar();

  const isDirty = () => {
    return imageFormData;
  };

  const resetData = () => {
    setImageFormData('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('SignedContract', imageFormData);

      await postSalesContractOffer(buildId, formData);

      await refetchApprovedDetails();
      await refetchContractDetails();
      resetData();
      setIsSaving(false);
      enqueueSnackbar('Success, offer created!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error creating offer, please try again.`, {
        variant: 'error'
      });
      setIsSaving(false);
      console.log(error);
    }
  };

  return (
    <>
      <Box mt={1}>
        {!isLoadingApprovedDetails && !isSaving && statusText && (
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.readOnlyHeaders} variant="h6">
                Build Info
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Status:</span> {statusText} <br />
              </Typography>
              {!!approvedStatusDetails?.contractPrice && (
                <Typography variant="subtitle1">
                  <span className={classes.detailsLabel}>Contract Price:</span>{' '}
                  {formatMoney(approvedStatusDetails?.contractPrice)} <br />
                </Typography>
              )}
              {approvedStatusDetails?.possibleLotFitIssuesText && (
                <Typography variant="subtitle1">
                  <span className={classes.detailsLabel}>Possible Lot Fit Issues:</span>{' '}
                  {approvedStatusDetails?.possibleLotFitIssuesText} <br />
                </Typography>
              )}
            </Grid>

            <Grid container className={classes.contractContainer}>
              <Grid item xs={12} sm={8} className={classes.signedContract}>
                <Grid container alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <SalesContractImages
                      imageTitle={'Signed Contract *'}
                      image={null}
                      imageData={value => setImageFormData(value)}
                      uploadText={'Upload Signed Contract'}
                    ></SalesContractImages>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      {!isLoadingApprovedDetails && !isSaving && statusText && (
        <ButtonGroup className={classes.buttonGroup}>
          <Button
            disabled={!imageFormData}
            type="submit"
            startIcon={<Save />}
            variant="contained"
            color="primary"
            onClick={() => {
              handleSave();
            }}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="contained"
            color="inherit"
            startIcon={<Close />}
            onClick={() => {
              if (!isDirty()) {
                history.push('/sales/contracting/contract-acceptance');
              } else {
                const result = window.confirm(
                  'You have unsaved changes, are you sure you want to exit?'
                );
                if (result) {
                  history.push('/sales/contracting/contract-acceptance');
                } else {
                  return;
                }
              }
            }}
          >
            Cancel
          </Button>
        </ButtonGroup>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1)
  },
  contractInfo: {
    fontWeight: '500',
    paddingRight: 8
  },
  readOnlyHeaders: {
    paddingBottom: theme.spacing(0.25),
    fontWeight: '700'
  },
  notSelected: {
    color: '#88888',
    opacity: 0.6
  },
  buttonText: {
    color: 'white'
  },
  detailsLabel: {
    fontWeight: 'bold'
  },
  offerAccepted: {
    '@media (min-width: 600px)': {
      marginTop: '-10px',
      paddingLeft: '30px'
    }
  },
  prevOffers: {
    '@media (min-width: 600px)': {
      paddingLeft: '30px'
    }
  },
  counterOffer: {
    paddingBottom: '0px'
  },
  signedContract: {
    paddingBottom: '16px'
  },
  contractContainer: {
    marginTop: '-30px'
  },
  buttonGroup: {
    paddingTop: '16px'
  }
}));
